import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/lib/auth-context";
import {
  useConversations, useMessages, usePresence, formatTimestamp,
  createConversation, searchUsers, type ConversationWithMeta, type MessagePriority,
  type MessageWithMeta,
} from "@/lib/messaging";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare, Send, Search, X, Plus, Users, Bell,
  CheckCheck, Check, ArrowLeft, Paperclip, Smile, MoreHorizontal,
  Pin, Star, Archive, Trash2, Reply, Forward, Copy, AlertCircle,
  Zap, User, Inbox, Filter, Phone, Mail, Building2, Store,
  ChevronRight, PanelRightOpen, PanelRightClose, Command,
  Sparkles, Hash, AtSign, Settings, Volume2, VolumeX,
  CircleDot, ArrowUp, ArrowDown, Clock, Activity, TrendingUp,
  Briefcase, GraduationCap, Shield, FileText, Image as ImageIcon,
  Mic, Calendar, Hash as HashIcon, Bookmark, Eye, EyeOff,
  CornerDownLeft, ChevronsUpDown, ListFilter, Pin as PinIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { OmnichannelComposer } from "@/components/omnichannel-composer";

const FOREST = "#14532D";
const FOREST_DEEP = "#0C3320";
const FOREST_MID = "#1E7A45";
const FOREST_TINT = "#E8F1EA";
const ACCENT_GOLD = "#C28B3A";
const TERRACOTTA = "#CC746A";
const INK = "#101511";
const MUTED = "#667069";
const BORDER = "#E2E7E3";
const BG_SUBTLE = "#F4F7F5";
const BG_CHAT = "#FAFBFA";
const BG_DARK = "#0C3320";

function getInitials(name: string): string {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    parent: "Parent", student: "Student", school_admin: "School Admin",
    merchant: "Merchant", superadmin: "Admin", finance_officer: "Finance Officer",
  };
  return map[role] || role;
}

function roleIcon(role: string): any {
  const map: Record<string, any> = {
    parent: User, student: GraduationCap, school_admin: Building2,
    merchant: Store, superadmin: Shield, finance_officer: Briefcase,
  };
  return map[role] || User;
}

function presenceDot(status: string | undefined): string {
  if (status === "online") return "#22c55e";
  if (status === "away") return "#eab308";
  return "#94a3b8";
}

function presenceLabel(status: string | undefined, lastSeen: string | undefined): string {
  if (status === "online") return "Online";
  if (status === "away") return "Away";
  if (lastSeen) return `Last seen ${formatTimestamp(lastSeen)}`;
  return "Offline";
}

function priorityRank(conv: ConversationWithMeta): number {
  const p = conv.last_message?.priority;
  if (p === "urgent") return 3;
  if (p === "high") return 2;
  if (conv.unread_count > 0) return 1;
  return 0;
}

type SidebarFilter = "inbox" | "unread" | "starred" | "groups" | "archived" | "announcements" | "pinned";
type ContextTab = "about" | "members" | "files" | "activity" | "settings";

export function MessagingCentrePage() {
  const { user, profile } = useAuth();
  const { conversations, loading } = useConversations();
  const { getPresence } = usePresence();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<SidebarFilter>("inbox");
  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [showContext, setShowContext] = useState(true);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [showCommand, setShowCommand] = useState(false);
  const [pinnedConvIds, setPinnedConvIds] = useState<Set<string>>(new Set());
  const [starredConvIds, setStarredConvIds] = useState<Set<string>>(new Set());

  const isSuperadmin = profile?.role === "superadmin";

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowCommand(true);
      }
      if (e.key === "Escape") setShowCommand(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const sortedConversations = useMemo(() => {
    return [...conversations].sort((a, b) => {
      const aPin = pinnedConvIds.has(a.id) ? 1 : 0;
      const bPin = pinnedConvIds.has(b.id) ? 1 : 0;
      if (aPin !== bPin) return bPin - aPin;
      return priorityRank(b) - priorityRank(a);
    });
  }, [conversations, pinnedConvIds]);

  const filtered = useMemo(() => {
    let list = sortedConversations;
    if (filter === "unread") list = list.filter((c) => c.unread_count > 0);
    else if (filter === "groups") list = list.filter((c) => c.type === "group");
    else if (filter === "starred") list = list.filter((c) => starredConvIds.has(c.id));
    else if (filter === "pinned") list = list.filter((c) => pinnedConvIds.has(c.id));
    else if (filter === "archived") list = list.filter((c) => c.members.some((m) => m.user_id === user?.id && m.archived));
    else if (filter === "announcements") list = list.filter((c) => c.type === "announcement");
    if (search.trim()) {
      list = list.filter(
        (c) =>
          (c.title || "").toLowerCase().includes(search.toLowerCase()) ||
          (c.last_message?.body || "").toLowerCase().includes(search.toLowerCase())
      );
    }
    return list;
  }, [sortedConversations, filter, search, user, starredConvIds, pinnedConvIds]);

  const selectedConv = conversations.find((c) => c.id === selectedConvId) || null;
  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  const togglePin = (id: string) => {
    setPinnedConvIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleStar = (id: string) => {
    setStarredConvIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  if (isSuperadmin) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-white" style={{ background: BG_SUBTLE }}>
        <div className="mb-8 text-center">
          <div className="relative mx-auto mb-5">
            <div className="grid h-20 w-20 place-items-center rounded-3xl" style={{ background: FOREST_TINT }}>
              <Sparkles className="h-10 w-10" style={{ color: FOREST }} />
            </div>
            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full text-white shadow-lg"
              style={{ background: ACCENT_GOLD }}
            >
              <Send className="h-3.5 w-3.5" />
            </motion.div>
          </div>
          <h2 className="font-display text-2xl font-bold mb-1" style={{ color: INK }}>Omnichannel Broadcast</h2>
          <p className="text-sm max-w-md mx-auto" style={{ color: MUTED }}>
            Compose and send messages across email, SMS, WhatsApp, in-app and push notifications to your entire audience.
          </p>
        </div>
        <Button
          className="text-white"
          size="lg"
          style={{ background: FOREST, border: "none" }}
          onClick={() => setShowBroadcast(true)}
        >
          <Sparkles className="mr-2 h-5 w-5" /> New Broadcast
        </Button>

        <OmnichannelComposer
          open={showBroadcast}
          onClose={() => setShowBroadcast(false)}
          onSend={(data) => { setShowBroadcast(false); }}
        />
      </div>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-white">
      {/* Column 1 — Smart Inbox */}
      <div className="flex w-full max-w-[340px] shrink-0 flex-col border-r" style={{ borderColor: BORDER }}>
        {/* Header with command bar */}
        <div className="px-4 pt-4 pb-3 border-b" style={{ borderColor: BORDER }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-xl" style={{ background: FOREST }}>
                <MessageSquare className="h-4 w-4 text-white" />
              </div>
              <h1 className="font-display text-xl font-bold" style={{ color: INK }}>Messages</h1>
              {totalUnread > 0 && (
                <span className="grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-[10px] font-bold text-white" style={{ background: TERRACOTTA }}>
                  {totalUnread}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowBroadcast(true)}
                className="grid h-8 w-8 place-items-center rounded-full transition hover:opacity-90"
                style={{ background: FOREST_TINT, color: FOREST }}
                title="Broadcast message"
              >
                <Sparkles className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowNewMessage(true)}
                className="grid h-8 w-8 place-items-center rounded-full text-white transition hover:opacity-90"
                style={{ background: FOREST }}
                title="New message"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Command palette trigger */}
          <button
            onClick={() => setShowCommand(true)}
            className="flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition hover:bg-slate-50"
            style={{ borderColor: BORDER, color: MUTED }}
          >
            <Search className="h-3.5 w-3.5" />
            <span className="flex-1">Search or jump to...</span>
            <kbd className="flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[9px] font-medium" style={{ borderColor: BORDER, color: MUTED }}>
              <Command className="h-2.5 w-2.5" />K
            </kbd>
          </button>
        </div>

        {/* Smart filters */}
        <div className="flex gap-1 px-3 py-2 overflow-x-auto border-b" style={{ borderColor: BORDER }}>
          {([
            { key: "inbox", label: "Inbox", icon: Inbox, count: conversations.length },
            { key: "unread", label: "Unread", icon: Bell, count: totalUnread },
            { key: "pinned", label: "Pinned", icon: Pin, count: pinnedConvIds.size },
            { key: "starred", label: "Starred", icon: Star, count: starredConvIds.size },
            { key: "groups", label: "Groups", icon: Users, count: conversations.filter(c => c.type === "group").length },
            { key: "announcements", label: "System", icon: AlertCircle, count: conversations.filter(c => c.type === "announcement").length },
          ] as const).map((tab) => {
            const active = filter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  active ? "text-white" : "text-slate-600 hover:bg-slate-100"
                }`}
                style={active ? { background: FOREST } : undefined}
              >
                <tab.icon className="h-3 w-3" />
                {tab.label}
                {tab.count > 0 && (
                  <span className={`text-[9px] ${active ? "text-white/60" : ""}`} style={{ color: active ? "rgba(255,255,255,0.5)" : MUTED }}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Conversation list */}
        <ScrollArea className="flex-1">
          {loading && (
            <div className="space-y-2 p-4">
              {[0,1,2,3,4].map(i => (
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="h-11 w-11 rounded-full bg-slate-100" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-2/3 rounded bg-slate-100" />
                    <div className="h-2.5 w-full rounded bg-slate-50" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="flex flex-col items-center py-16">
              <div className="grid h-14 w-14 place-items-center rounded-2xl mb-3" style={{ background: FOREST_TINT }}>
                <MessageSquare className="h-7 w-7" style={{ color: FOREST }} />
              </div>
              <p className="text-sm font-medium" style={{ color: INK }}>No conversations</p>
              <p className="text-xs mt-1" style={{ color: MUTED }}>Start a new conversation</p>
            </div>
          )}
          {!loading && filtered.map((conv) => {
            const otherUser = conv.members.find((m) => m.user_id !== user?.id);
            const presence = getPresence(otherUser?.user_id || "");
            const isGroup = conv.type === "group";
            const isAnnouncement = conv.type === "announcement";
            const displayName = conv.title || "Direct";
            const isSelected = conv.id === selectedConvId;
            const isPinned = pinnedConvIds.has(conv.id);
            const isStarred = starredConvIds.has(conv.id);
            const priority = conv.last_message?.priority;
            const RoleIcon = otherUser ? roleIcon(roleLabel("parent")) : null;

            return (
              <div
                key={conv.id}
                onClick={() => setSelectedConvId(conv.id)}
                className={`group relative flex cursor-pointer items-start gap-3 px-4 py-3 transition ${
                  isSelected ? "" : "hover:bg-slate-50"
                }`}
                style={isSelected ? { background: FOREST_TINT } : undefined}
              >
                {/* Priority indicator bar */}
                {priority === "urgent" && (
                  <div className="absolute left-0 top-0 h-full w-1" style={{ background: TERRACOTTA }} />
                )}
                {priority === "high" && !isPinned && (
                  <div className="absolute left-0 top-0 h-full w-1" style={{ background: ACCENT_GOLD }} />
                )}
                {isPinned && !priority && (
                  <div className="absolute left-0 top-0 h-full w-1" style={{ background: FOREST_MID }} />
                )}

                <div className="relative shrink-0">
                  {isGroup || isAnnouncement ? (
                    <div
                      className="grid h-11 w-11 place-items-center rounded-full"
                      style={{ background: isAnnouncement ? `${ACCENT_GOLD}15` : FOREST_TINT }}
                    >
                      {isAnnouncement ? (
                        <AlertCircle className="h-5 w-5" style={{ color: ACCENT_GOLD }} />
                      ) : (
                        <Users className="h-5 w-5" style={{ color: FOREST }} />
                      )}
                    </div>
                  ) : (
                    <Avatar className="h-11 w-11">
                      <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "0.75rem", fontWeight: 600 }}>
                        {getInitials(displayName)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  {!isGroup && !isAnnouncement && (
                    <span
                      className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white"
                      style={{ background: presenceDot(presence?.status) }}
                    />
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {isStarred && <Star className="h-3 w-3 shrink-0 fill-current" style={{ color: ACCENT_GOLD }} />}
                      <span className="truncate text-sm font-semibold" style={{ color: INK }}>{displayName}</span>
                      {isPinned && <Pin className="h-3 w-3 shrink-0" style={{ color: FOREST_MID }} />}
                    </div>
                    <span className="shrink-0 text-[10px]" style={{ color: MUTED }}>
                      {conv.last_message ? formatTimestamp(conv.last_message.created_at) : ""}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <div className="flex items-center gap-1 min-w-0">
                      {priority === "urgent" && <Zap className="h-3 w-3 shrink-0" style={{ color: TERRACOTTA }} />}
                      <span className="truncate text-xs" style={{ color: MUTED }}>
                        {conv.last_message?.body || "No messages yet"}
                      </span>
                    </div>
                    {conv.unread_count > 0 && (
                      <span
                        className="grid h-5 min-w-5 shrink-0 place-items-center rounded-full px-1.5 text-[10px] font-bold text-white"
                        style={{ background: TERRACOTTA }}
                      >
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>

                {/* Hover actions */}
                <div className="absolute right-2 top-2 flex gap-0.5 opacity-0 transition group-hover:opacity-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); togglePin(conv.id); }}
                    className="grid h-6 w-6 place-items-center rounded-full bg-white shadow-sm hover:bg-slate-100"
                    style={{ border: `1px solid ${BORDER}` }}
                    title={isPinned ? "Unpin" : "Pin"}
                  >
                    <Pin className="h-3 w-3" style={{ color: isPinned ? FOREST_MID : MUTED }} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleStar(conv.id); }}
                    className="grid h-6 w-6 place-items-center rounded-full bg-white shadow-sm hover:bg-slate-100"
                    style={{ border: `1px solid ${BORDER}` }}
                    title={isStarred ? "Unstar" : "Star"}
                  >
                    <Star className="h-3 w-3" style={{ color: isStarred ? ACCENT_GOLD : MUTED, fill: isStarred ? ACCENT_GOLD : "none" }} />
                  </button>
                </div>
              </div>
            );
          })}
        </ScrollArea>

        {/* Footer status */}
        <div className="border-t px-4 py-2 flex items-center justify-between" style={{ borderColor: BORDER }}>
          <div className="flex items-center gap-1.5 text-[10px]" style={{ color: MUTED }}>
            <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
            Realtime connected
          </div>
          <button className="grid h-6 w-6 place-items-center rounded-full hover:bg-slate-100" title="Settings">
            <Settings className="h-3.5 w-3.5" style={{ color: MUTED }} />
          </button>
        </div>
      </div>

      {/* Column 2 — Conversation */}
      <div className="flex min-w-0 flex-1 flex-col">
        {selectedConv ? (
          <ConversationView
            conv={selectedConv}
            onToggleContext={() => setShowContext(!showContext)}
            showContext={showContext}
            isPinned={pinnedConvIds.has(selectedConv.id)}
            onTogglePin={() => togglePin(selectedConv.id)}
          />
        ) : (
          <EmptyState onNewMessage={() => setShowNewMessage(true)} />
        )}
      </div>

      {/* Column 3 — Context */}
      {selectedConv && showContext && (
        <ContextPanel conv={selectedConv} onClose={() => setShowContext(false)} />
      )}

      {/* New Message Modal */}
      {showNewMessage && (
        <NewMessageModal
          onClose={() => setShowNewMessage(false)}
          onCreated={(id) => { setSelectedConvId(id); setShowNewMessage(false); }}
        />
      )}

      {/* Broadcast / Omnichannel Composer */}
      <OmnichannelComposer
        open={showBroadcast}
        onClose={() => setShowBroadcast(false)}
        onSend={(data) => { setShowBroadcast(false); }}
      />

      {/* Command Palette */}
      {showCommand && (
        <CommandPalette
          conversations={conversations}
          onClose={() => setShowCommand(false)}
          onSelect={(id) => { setSelectedConvId(id); setShowCommand(false); }}
          onNewMessage={() => { setShowCommand(false); setShowNewMessage(true); }}
        />
      )}
    </div>
  );
}

function EmptyState({ onNewMessage }: { onNewMessage: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center" style={{ background: BG_CHAT }}>
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="text-center"
      >
        <div className="relative mx-auto mb-6">
          <div className="grid h-20 w-20 place-items-center rounded-3xl" style={{ background: FOREST_TINT }}>
            <MessageSquare className="h-10 w-10" style={{ color: FOREST }} />
          </div>
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute -right-2 -top-2 grid h-7 w-7 place-items-center rounded-full text-white shadow-lg"
            style={{ background: ACCENT_GOLD }}
          >
            <Sparkles className="h-3.5 w-3.5" />
          </motion.div>
        </div>
        <h2 className="font-display text-2xl font-bold mb-1" style={{ color: INK }}>Your inbox is ready</h2>
        <p className="text-sm max-w-xs mx-auto" style={{ color: MUTED }}>
          Select a conversation or start a new one. Messages are delivered in real time.
        </p>
        <Button className="mt-5 text-white" style={{ background: FOREST, border: "none" }} onClick={onNewMessage}>
          <Plus className="mr-1.5 h-4 w-4" /> New Message
        </Button>
        <div className="mt-6 flex items-center gap-2 text-[11px]" style={{ color: MUTED }}>
          <kbd className="flex items-center gap-0.5 rounded border px-1.5 py-0.5" style={{ borderColor: BORDER }}>
            <Command className="h-2.5 w-2.5" />K
          </kbd>
          to search
        </div>
      </motion.div>
    </div>
  );
}

function ConversationView({
  conv,
  onToggleContext,
  showContext,
  isPinned,
  onTogglePin,
}: {
  conv: ConversationWithMeta;
  onToggleContext: () => void;
  showContext: boolean;
  isPinned: boolean;
  onTogglePin: () => void;
}) {
  const { user, profile } = useAuth();
  const { messages, loading, sendMessage, markAsRead, deleteMessage, toggleReaction, sendTyping, stopTyping, typingUser } =
    useMessages(conv.id);
  const { getPresence } = usePresence();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [replyText, setReplyText] = useState("");
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showPriorityMenu, setShowPriorityMenu] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState<MessagePriority>("normal");
  const [showConvMenu, setShowConvMenu] = useState(false);
  const [muted, setMuted] = useState(false);

  useEffect(() => { markAsRead(); }, [markAsRead, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, typingUser]);

  const isGroup = conv.type === "group";
  const isAnnouncement = conv.type === "announcement";
  const otherMember = conv.members.find((m) => m.user_id !== user?.id);
  const displayName = conv.title || "Direct";
  const presence = getPresence(otherMember?.user_id || "");

  const handleSend = async () => {
    if (!replyText.trim()) return;
    try {
      await sendMessage(replyText, selectedPriority, replyTo);
      setReplyText("");
      setReplyTo(null);
      setSelectedPriority("normal");
      stopTyping();
    } catch { toast.error("Failed to send message"); }
  };

  const handleDelete = async (messageId: string) => {
    try { await deleteMessage(messageId); toast.success("Message deleted"); }
    catch { toast.error("Failed to delete message"); }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const insertEmoji = (emoji: string) => {
    setReplyText(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const reactions = ["👍", "❤️", "🎉", "✅", "👀", "🔥"];

  return (
    <>
      {/* Conversation header */}
      <div className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: BORDER }}>
        <div className="relative">
          {isGroup || isAnnouncement ? (
            <div className="grid h-10 w-10 place-items-center rounded-full" style={{ background: isAnnouncement ? `${ACCENT_GOLD}15` : FOREST_TINT }}>
              {isAnnouncement ? <AlertCircle className="h-5 w-5" style={{ color: ACCENT_GOLD }} /> : <Users className="h-5 w-5" style={{ color: FOREST }} />}
            </div>
          ) : (
            <Avatar className="h-10 w-10">
              <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "0.75rem", fontWeight: 600 }}>
                {getInitials(displayName)}
              </AvatarFallback>
            </Avatar>
          )}
          {!isGroup && !isAnnouncement && (
            <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white" style={{ background: presenceDot(presence?.status) }} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold" style={{ color: INK }}>{displayName}</span>
            {isPinned && <Pin className="h-3 w-3" style={{ color: FOREST_MID }} />}
            {muted && <VolumeX className="h-3 w-3" style={{ color: MUTED }} />}
          </div>
          {!isGroup && !isAnnouncement && (
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: MUTED }}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: presenceDot(presence?.status) }} />
              {presenceLabel(presence?.status, presence?.last_seen_at)}
            </div>
          )}
          {isGroup && <div className="text-[11px]" style={{ color: MUTED }}>{conv.members.length} members</div>}
        </div>

        {/* Header actions */}
        <div className="flex items-center gap-1">
          <button onClick={onTogglePin} className="grid h-8 w-8 place-items-center rounded-full transition hover:bg-slate-100" title={isPinned ? "Unpin" : "Pin conversation"}>
            <Pin className="h-4 w-4" style={{ color: isPinned ? FOREST_MID : MUTED }} />
          </button>
          <button onClick={() => setMuted(!muted)} className="grid h-8 w-8 place-items-center rounded-full transition hover:bg-slate-100" title={muted ? "Unmute" : "Mute"}>
            {muted ? <VolumeX className="h-4 w-4" style={{ color: MUTED }} /> : <Volume2 className="h-4 w-4" style={{ color: MUTED }} />}
          </button>
          <div className="relative">
            <button onClick={() => setShowConvMenu(!showConvMenu)} className="grid h-8 w-8 place-items-center rounded-full transition hover:bg-slate-100" title="More">
              <MoreHorizontal className="h-4 w-4" style={{ color: MUTED }} />
            </button>
            {showConvMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowConvMenu(false)} />
                <motion.div
                  initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                  className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl border bg-white py-1 shadow-xl"
                  style={{ borderColor: BORDER }}
                >
                  <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50" style={{ color: INK }}>
                    <Bookmark className="h-3.5 w-3.5" style={{ color: MUTED }} /> Save messages
                  </button>
                  <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50" style={{ color: INK }}>
                    <EyeOff className="h-3.5 w-3.5" style={{ color: MUTED }} /> Mark unread
                  </button>
                  <Separator />
                  <button className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50" style={{ color: TERRACOTTA }}>
                    <Archive className="h-3.5 w-3.5" /> Archive
                  </button>
                </motion.div>
              </>
            )}
          </div>
          <button onClick={onToggleContext} className={`grid h-8 w-8 place-items-center rounded-full transition hover:bg-slate-100 ${showContext ? "opacity-40" : ""}`} title="Toggle context">
            <PanelRightOpen className="h-4 w-4" style={{ color: MUTED }} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-1" style={{ background: BG_CHAT }}>
        {loading && <div className="text-center text-sm py-8" style={{ color: MUTED }}>Loading messages...</div>}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center py-16">
            <div className="grid h-12 w-12 place-items-center rounded-2xl mb-3" style={{ background: FOREST_TINT }}>
              <MessageSquare className="h-6 w-6" style={{ color: FOREST }} />
            </div>
            <p className="text-sm font-medium" style={{ color: INK }}>No messages yet</p>
            <p className="text-xs mt-1" style={{ color: MUTED }}>Send the first message below</p>
          </div>
        )}
        {messages.map((msg, idx) => {
          const isMine = msg.sender_id === user?.id;
          const isDeleted = !!msg.deleted_at;
          const isAnn = msg.message_type === "announcement";
          const isUrgent = msg.priority === "urgent";
          const isHigh = msg.priority === "high";
          const prevMsg = idx > 0 ? messages[idx - 1] : null;
          const showDateDivider = !prevMsg || new Date(prevMsg.created_at).toDateString() !== new Date(msg.created_at).toDateString();
          const showSenderName = !isMine && (isGroup || (prevMsg && prevMsg.sender_id !== msg.sender_id));

          return (
            <div key={msg.id}>
              {/* Date divider */}
              {showDateDivider && (
                <div className="flex items-center justify-center my-4">
                  <div className="flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-medium" style={{ background: BG_SUBTLE, color: MUTED }}>
                    <Clock className="h-3 w-3" />
                    {new Date(msg.created_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                  </div>
                </div>
              )}
              <div className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[70%] ${isMine ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
                  {showSenderName && !isDeleted && (
                    <div className="flex items-center gap-1.5 px-1">
                      <Avatar className="h-4 w-4">
                        <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "0.45rem", fontWeight: 600 }}>
                          {getInitials(msg.sender_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-[10px] font-medium" style={{ color: isGroup ? FOREST_MID : MUTED }}>{msg.sender_name}</span>
                      {msg.sender_role && (
                        <span className="text-[8px] rounded px-1 py-0.5" style={{ background: FOREST_TINT, color: FOREST_MID }}>
                          {roleLabel(msg.sender_role)}
                        </span>
                      )}
                    </div>
                  )}
                  {isAnn && (
                    <div className="flex items-center gap-1.5 mb-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${ACCENT_GOLD}15`, color: ACCENT_GOLD }}>
                      <AlertCircle className="h-3 w-3" /> Announcement
                    </div>
                  )}
                  {isUrgent && !isAnn && (
                    <div className="flex items-center gap-1 mb-0.5 text-[10px] font-semibold" style={{ color: TERRACOTTA }}>
                      <Zap className="h-3 w-3" /> Urgent
                    </div>
                  )}
                  {isHigh && !isAnn && !isUrgent && (
                    <div className="flex items-center gap-1 mb-0.5 text-[10px] font-semibold" style={{ color: ACCENT_GOLD }}>
                      <Pin className="h-3 w-3" /> High priority
                    </div>
                  )}
                  <motion.div
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15 }}
                    className={`group relative rounded-2xl px-3.5 py-2 text-sm ${isMine ? "rounded-br-md" : "rounded-bl-md"}`}
                    style={{
                      background: isAnn ? `${ACCENT_GOLD}12` : isMine ? FOREST : "white",
                      color: isAnn ? INK : isMine ? "white" : INK,
                      border: isAnn ? `1px solid ${ACCENT_GOLD}40` : isMine ? "none" : `1px solid ${BORDER}`,
                    }}
                  >
                    {isDeleted ? (
                      <span className="italic text-xs" style={{ color: MUTED }}>Message deleted</span>
                    ) : (
                      <>
                        {msg.reply_to && (
                          <div className="mb-1.5 rounded-lg px-2 py-1 text-[11px] opacity-60" style={{ background: isMine ? "rgba(255,255,255,0.1)" : BG_SUBTLE }}>
                            <Reply className="inline h-2.5 w-2.5 mr-1" />
                            {msg.reply_to.body.slice(0, 100)}
                          </div>
                        )}
                        {msg.body}
                      </>
                    )}
                    {/* Hover actions */}
                    {!isDeleted && (
                      <div className="absolute -top-3 right-1 flex items-center gap-0.5 rounded-full bg-white px-1 py-0.5 opacity-0 shadow-sm transition group-hover:opacity-100" style={{ border: `1px solid ${BORDER}` }}>
                        <button onClick={() => toggleReaction(msg.id, "👍")} className="grid h-5 w-5 place-items-center rounded-full hover:bg-slate-100" title="React">
                          <Smile className="h-3 w-3" style={{ color: MUTED }} />
                        </button>
                        <button onClick={() => setReplyTo(msg.id)} className="grid h-5 w-5 place-items-center rounded-full hover:bg-slate-100" title="Reply">
                          <Reply className="h-3 w-3" style={{ color: MUTED }} />
                        </button>
                        <button onClick={() => handleCopy(msg.body)} className="grid h-5 w-5 place-items-center rounded-full hover:bg-slate-100" title="Copy">
                          <Copy className="h-3 w-3" style={{ color: MUTED }} />
                        </button>
                        {isMine && (
                          <button onClick={() => handleDelete(msg.id)} className="grid h-5 w-5 place-items-center rounded-full hover:bg-slate-100" title="Delete">
                            <Trash2 className="h-3 w-3" style={{ color: TERRACOTTA }} />
                          </button>
                        )}
                      </div>
                    )}
                  </motion.div>
                  {/* Reactions */}
                  {msg.reactions.length > 0 && !isDeleted && (
                    <div className="flex gap-1 mt-0.5">
                      {Object.entries(msg.reactions.reduce((acc: Record<string, number>, r) => {
                        acc[r.reaction] = (acc[r.reaction] || 0) + 1;
                        return acc;
                      }, {})).map(([emoji, count]) => (
                        <span key={emoji} className="flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs" style={{ background: BG_SUBTLE }}>
                          {emoji} {count > 1 && <span className="text-[10px]" style={{ color: MUTED }}>{count}</span>}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Timestamp + read status */}
                  <div className={`flex items-center gap-1 px-1 ${isMine ? "flex-row-reverse" : ""}`}>
                    <span className="text-[9px]" style={{ color: MUTED }}>{formatTimestamp(msg.created_at)}</span>
                    {isMine && !isDeleted && <CheckCheck className="h-3 w-3" style={{ color: FOREST_MID }} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {/* Typing indicator */}
        {typingUser && (
          <div className="flex items-center gap-2 px-2">
            <span className="text-xs italic" style={{ color: MUTED }}>{typingUser} is typing...</span>
            <div className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <motion.span key={i} animate={{ y: [0, -3, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }} className="h-1.5 w-1.5 rounded-full" style={{ background: MUTED }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reply preview */}
      {replyTo && (
        <div className="flex items-center gap-2 border-t px-4 py-2" style={{ borderColor: BORDER, background: BG_SUBTLE }}>
          <Reply className="h-3.5 w-3.5" style={{ color: MUTED }} />
          <span className="text-xs truncate flex-1" style={{ color: MUTED }}>
            Replying to: {messages.find((m) => m.id === replyTo)?.body.slice(0, 80) || ""}
          </span>
          <button onClick={() => setReplyTo(null)} className="grid h-6 w-6 place-items-center rounded-full hover:bg-slate-200">
            <X className="h-3 w-3" style={{ color: MUTED }} />
          </button>
        </div>
      )}

      {/* Smart composer */}
      <div className="border-t px-4 py-3" style={{ borderColor: BORDER }}>
        <div className="flex items-end gap-2">
          <button className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-slate-100" title="Attach file">
            <Paperclip className="h-4 w-4" style={{ color: MUTED }} />
          </button>

          {/* Priority indicator */}
          <div className="relative">
            <button
              onClick={() => setShowPriorityMenu(!showPriorityMenu)}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full hover:bg-slate-100"
              title="Set priority"
              style={selectedPriority !== "normal" ? { background: selectedPriority === "urgent" ? `${TERRACOTTA}15` : `${ACCENT_GOLD}15` } : undefined}
            >
              {selectedPriority === "urgent" ? <Zap className="h-4 w-4" style={{ color: TERRACOTTA }} /> :
               selectedPriority === "high" ? <Pin className="h-4 w-4" style={{ color: ACCENT_GOLD }} /> :
               <Hash className="h-4 w-4" style={{ color: MUTED }} />}
            </button>
            {showPriorityMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowPriorityMenu(false)} />
                <motion.div
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="absolute bottom-full left-0 mb-1 z-20 w-36 rounded-xl border bg-white py-1 shadow-xl"
                  style={{ borderColor: BORDER }}
                >
                  {([
                    { value: "normal", label: "Normal", icon: Hash, color: MUTED },
                    { value: "high", label: "High", icon: Pin, color: ACCENT_GOLD },
                    { value: "urgent", label: "Urgent", icon: Zap, color: TERRACOTTA },
                  ] as const).map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setSelectedPriority(opt.value); setShowPriorityMenu(false); }}
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-slate-50"
                      style={{ color: selectedPriority === opt.value ? opt.color : INK }}
                    >
                      <opt.icon className="h-3.5 w-3.5" style={{ color: opt.color }} />
                      {opt.label}
                      {selectedPriority === opt.value && <Check className="h-3 w-3 ml-auto" style={{ color: opt.color }} />}
                    </button>
                  ))}
                </motion.div>
              </>
            )}
          </div>

          <div className="relative flex-1">
            <Input
              placeholder="Type a message... (use / for commands)"
              value={replyText}
              onChange={(e) => {
                setReplyText(e.target.value);
                if (e.target.value.startsWith("/")) setShowSlashMenu(true); else setShowSlashMenu(false);
                if (e.target.value.length > 0) sendTyping(profile?.full_name || "User");
                else stopTyping();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              className="pr-10"
            />
            {/* Emoji button */}
            <div className="absolute right-1 top-1/2 -translate-y-1/2">
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="grid h-7 w-7 place-items-center rounded-full hover:bg-slate-100"
                title="Add emoji"
              >
                <Smile className="h-4 w-4" style={{ color: MUTED }} />
              </button>
              {showEmojiPicker && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowEmojiPicker(false)} />
                  <motion.div
                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-full right-0 mb-1 z-20 grid grid-cols-6 gap-1 rounded-xl border bg-white p-2 shadow-xl"
                    style={{ borderColor: BORDER }}
                  >
                    {reactions.map(emoji => (
                      <button key={emoji} onClick={() => insertEmoji(emoji)} className="grid h-8 w-8 place-items-center rounded-lg text-lg hover:bg-slate-100">
                        {emoji}
                      </button>
                    ))}
                  </motion.div>
                </>
              )}
            </div>
          </div>

          <button
            onClick={handleSend}
            disabled={!replyText.trim()}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-white transition disabled:opacity-40"
            style={{ background: FOREST }}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>

        {/* Slash command menu */}
        {showSlashMenu && (
          <motion.div
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            className="mt-2 rounded-xl border bg-white p-2 shadow-lg" style={{ borderColor: BORDER }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-1 px-2" style={{ color: MUTED }}>Quick commands</div>
            {[
              { cmd: "/announce", desc: "Send as announcement", icon: AlertCircle },
              { cmd: "/urgent", desc: "Mark as urgent", icon: Zap },
              { cmd: "/pin", desc: "Pin this conversation", icon: Pin },
            ].map(c => (
              <button
                key={c.cmd}
                onClick={() => { setReplyText(""); setShowSlashMenu(false); if (c.cmd === "/urgent") setSelectedPriority("urgent"); if (c.cmd === "/pin") onTogglePin(); }}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-slate-50"
              >
                <c.icon className="h-3.5 w-3.5" style={{ color: FOREST_MID }} />
                <span className="font-mono font-medium" style={{ color: INK }}>{c.cmd}</span>
                <span style={{ color: MUTED }}>{c.desc}</span>
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </>
  );
}

function ContextPanel({ conv, onClose }: { conv: ConversationWithMeta; onClose: () => void }) {
  const { user } = useAuth();
  const { getPresence } = usePresence();
  const [activeTab, setActiveTab] = useState<ContextTab>("about");
  const [memberProfiles, setMemberProfiles] = useState<Array<{ id: string; full_name: string; role: string }>>([]);

  useEffect(() => {
    const fetchProfiles = async () => {
      const memberIds = conv.members.map((m) => m.user_id);
      const { data: profiles } = await supabase.from("profiles").select("id, full_name").in("id", memberIds);
      const { data: roles } = await supabase.from("user_roles").select("user_id, role").in("user_id", memberIds);
      const result = (profiles || []).map((p: any) => ({
        id: p.id,
        full_name: p.full_name || "Unknown",
        role: (roles || []).find((r: any) => r.user_id === p.id)?.role || "user",
      }));
      setMemberProfiles(result);
    };
    fetchProfiles();
  }, [conv.id]);

  const otherMembers = memberProfiles.filter((m) => m.id !== user?.id);
  const isGroup = conv.type === "group";
  const primaryMember = otherMembers[0];

  const tabs: { key: ContextTab; label: string; icon: any }[] = [
    { key: "about", label: "About", icon: User },
    { key: "members", label: "Members", icon: Users },
    { key: "files", label: "Files", icon: FileText },
    { key: "activity", label: "Activity", icon: Activity },
    { key: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="hidden w-80 shrink-0 flex-col border-l xl:flex" style={{ borderColor: BORDER }}>
      <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: BORDER }}>
        <span className="text-sm font-semibold" style={{ color: INK }}>Context</span>
        <button onClick={onClose} className="grid h-7 w-7 place-items-center rounded-full hover:bg-slate-100">
          <X className="h-3.5 w-3.5" style={{ color: MUTED }} />
        </button>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 border-b px-2 py-2" style={{ borderColor: BORDER }}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition ${
              activeTab === tab.key ? "text-white" : "hover:bg-slate-100"
            }`}
            style={activeTab === tab.key ? { background: FOREST } : { color: MUTED }}
          >
            <tab.icon className="h-3 w-3" />
            {tab.label}
          </button>
        ))}
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {activeTab === "about" && (
            <>
              {primaryMember && (
                <div className="flex flex-col items-center text-center">
                  <Avatar className="h-16 w-16 mb-2">
                    <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "1rem", fontWeight: 600 }}>
                      {getInitials(primaryMember.full_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-sm font-semibold" style={{ color: INK }}>{primaryMember.full_name}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {(() => {
                      const RoleIcon = roleIcon(primaryMember.role);
                      return <RoleIcon className="h-3 w-3" style={{ color: MUTED }} />;
                    })()}
                    <Badge variant="outline" className="text-[10px]">{roleLabel(primaryMember.role)}</Badge>
                  </div>
                  {(() => {
                    const p = getPresence(primaryMember.id);
                    return (
                      <div className="flex items-center gap-1.5 mt-2 text-[11px]" style={{ color: MUTED }}>
                        <span className="h-2 w-2 rounded-full" style={{ background: presenceDot(p?.status) }} />
                        {presenceLabel(p?.status, p?.last_seen_at)}
                      </div>
                    );
                  })()}
                </div>
              )}
              <Separator />
              <div className="space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Contact</div>
                <div className="flex items-center gap-2 text-xs" style={{ color: INK }}>
                  <Mail className="h-3.5 w-3.5" style={{ color: MUTED }} />
                  <span className="truncate">{primaryMember?.id.slice(0, 8)}@fundred.co</span>
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: INK }}>
                  <Phone className="h-3.5 w-3.5" style={{ color: MUTED }} />
                  <span>Not available</span>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Conversation</div>
                <div className="flex items-center gap-2 text-xs" style={{ color: INK }}>
                  <MessageSquare className="h-3.5 w-3.5" style={{ color: MUTED }} />
                  <span>{conv.type === "group" ? "Group" : conv.type === "announcement" ? "Announcement" : "Direct"}</span>
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: INK }}>
                  <Clock className="h-3.5 w-3.5" style={{ color: MUTED }} />
                  <span>Created {formatTimestamp(conv.created_at)}</span>
                </div>
              </div>
            </>
          )}

          {activeTab === "members" && (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>
                Members ({memberProfiles.length})
              </div>
              {memberProfiles.map((m) => {
                const p = getPresence(m.id);
                const RoleIcon = roleIcon(m.role);
                return (
                  <div key={m.id} className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-slate-50">
                    <div className="relative">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "0.6rem", fontWeight: 600 }}>
                          {getInitials(m.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white" style={{ background: presenceDot(p?.status) }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium" style={{ color: INK }}>{m.full_name}</div>
                      <div className="flex items-center gap-1">
                        <RoleIcon className="h-2.5 w-2.5" style={{ color: MUTED }} />
                        <span className="text-[10px]" style={{ color: MUTED }}>{roleLabel(m.role)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "files" && (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Shared files</div>
              <div className="flex flex-col items-center py-8">
                <FileText className="h-8 w-8 mb-2" style={{ color: "#cbd5e1" }} />
                <p className="text-xs" style={{ color: MUTED }}>No files shared yet</p>
              </div>
            </div>
          )}

          {activeTab === "activity" && (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Recent activity</div>
              <div className="flex flex-col items-center py-8">
                <Activity className="h-8 w-8 mb-2" style={{ color: "#cbd5e1" }} />
                <p className="text-xs" style={{ color: MUTED }}>No recent activity</p>
              </div>
            </div>
          )}

          {activeTab === "settings" && (
            <div className="space-y-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Conversation settings</div>
              {[
                { label: "Notifications", icon: Volume2, toggle: true },
                { label: "Read receipts", icon: Eye, toggle: true },
                { label: "Typing indicators", icon: Activity, toggle: true },
              ].map(s => (
                <div key={s.label} className="flex items-center justify-between rounded-lg p-2 hover:bg-slate-50">
                  <div className="flex items-center gap-2">
                    <s.icon className="h-3.5 w-3.5" style={{ color: MUTED }} />
                    <span className="text-xs" style={{ color: INK }}>{s.label}</span>
                  </div>
                  <div className="h-5 w-9 rounded-full p-0.5 cursor-pointer transition" style={{ background: FOREST_TINT }}>
                    <div className="h-4 w-4 rounded-full bg-white shadow-sm transition translate-x-4" style={{ background: FOREST }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function CommandPalette({
  conversations,
  onClose,
  onSelect,
  onNewMessage,
}: {
  conversations: ConversationWithMeta[];
  onClose: () => void;
  onSelect: (id: string) => void;
  onNewMessage: () => void;
}) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const results = useMemo(() => {
    if (!query.trim()) return conversations.slice(0, 8);
    return conversations.filter(c =>
      (c.title || "").toLowerCase().includes(query.toLowerCase()) ||
      (c.last_message?.body || "").toLowerCase().includes(query.toLowerCase())
    ).slice(0, 10);
  }, [query, conversations]);

  const commands = [
    { label: "New message", icon: Plus, action: onNewMessage },
    { label: "Go to inbox", icon: Inbox, action: () => {} },
    { label: "Search messages", icon: Search, action: () => {} },
  ];

  const allItems = [
    ...commands.filter(c => !query.trim() || c.label.toLowerCase().includes(query.toLowerCase())).map(c => ({ kind: "command" as const, ...c })),
    ...results.map(c => ({ kind: "conversation" as const, conv: c })),
  ];

  useEffect(() => { setSelectedIndex(0); }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, allItems.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
    if (e.key === "Enter") {
      e.preventDefault();
      const item = allItems[selectedIndex];
      if (item) {
        if (item.kind === "command") item.action();
        else if (item.kind === "conversation") onSelect(item.conv.id);
      }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        transition={{ duration: 0.15 }}
        className="fixed left-1/2 top-[15vh] z-[81] w-[92vw] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b px-4 py-3" style={{ borderColor: BORDER }}>
          <Search className="h-4 w-4" style={{ color: MUTED }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search conversations, people, or commands..."
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: INK }}
          />
          <kbd className="rounded border px-1.5 py-0.5 text-[9px] font-medium" style={{ borderColor: BORDER, color: MUTED }}>ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto p-2">
          {allItems.length === 0 && (
            <div className="py-8 text-center text-sm" style={{ color: MUTED }}>No results found</div>
          )}
          {allItems.map((item, idx) => {
            const isSel = idx === selectedIndex;
            if (item.kind === "command") {
              return (
                <button
                  key={item.label}
                  onClick={item.action}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${isSel ? "" : ""}`}
                  style={isSel ? { background: FOREST_TINT } : undefined}
                >
                  <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: FOREST_TINT }}>
                    <item.icon className="h-4 w-4" style={{ color: FOREST }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: INK }}>{item.label}</span>
                </button>
              );
            }
            const conv = item.conv;
            const displayName = conv.title || "Direct";
            const isGroup = conv.type === "group";
            return (
              <button
                key={conv.id}
                onClick={() => onSelect(conv.id)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition ${isSel ? "" : ""}`}
                style={isSel ? { background: FOREST_TINT } : undefined}
              >
                {isGroup ? (
                  <div className="grid h-8 w-8 place-items-center rounded-full" style={{ background: FOREST_TINT }}>
                    <Users className="h-4 w-4" style={{ color: FOREST }} />
                  </div>
                ) : (
                  <Avatar className="h-8 w-8">
                    <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "0.6rem", fontWeight: 600 }}>
                      {getInitials(displayName)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium" style={{ color: INK }}>{displayName}</div>
                  <div className="truncate text-xs" style={{ color: MUTED }}>{conv.last_message?.body || "No messages"}</div>
                </div>
                {conv.unread_count > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full px-1.5 text-[10px] font-bold text-white" style={{ background: TERRACOTTA }}>
                    {conv.unread_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t px-4 py-2 text-[10px]" style={{ borderColor: BORDER, color: MUTED }}>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="rounded border px-1" style={{ borderColor: BORDER }}>↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="rounded border px-1" style={{ borderColor: BORDER }}>↵</kbd> Select</span>
          </div>
          <span className="flex items-center gap-1"><Sparkles className="h-3 w-3" /> Smart search</span>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function NewMessageModal({ onClose, onCreated }: { onClose: () => void; onCreated: (convId: string) => void }) {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Array<{ id: string; full_name: string; role: string }>>([]);
  const [selected, setSelected] = useState<Array<{ id: string; full_name: string; role: string }>>([]);
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState<MessagePriority>("normal");
  const [isGroup, setIsGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (search.trim().length < 2) { setResults([]); return; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      const r = await searchUsers(search, user!.id);
      setResults(r);
      setSearching(false);
    }, 300);
  }, [search, user]);

  const toggleSelect = (person: { id: string; full_name: string; role: string }) => {
    setSelected(prev => {
      const exists = prev.find(p => p.id === person.id);
      if (exists) return prev.filter(p => p.id !== person.id);
      return [...prev, person];
    });
  };

  const handleSend = async () => {
    if (selected.length === 0 || !message.trim()) return;
    const memberIds = [user!.id, ...selected.map(s => s.id)];
    const type = selected.length > 1 || isGroup ? "group" : "direct";
    const title = type === "group" ? groupName || selected.map(s => s.full_name.split(" ")[0]).join(", ") : null;
    const convId = await createConversation(user!.id, memberIds, type as any, title);
    if (!convId) { toast.error("Failed to create conversation"); return; }
    const { error } = await supabase.from("messages").insert({
      conversation_id: convId, sender_id: user!.id, body: message.trim(), priority,
    });
    if (error) { toast.error("Failed to send message"); return; }
    toast.success("Message sent");
    onCreated(convId);
  };

  const priorityOptions: { value: MessagePriority; label: string; icon: any; color: string }[] = [
    { value: "normal", label: "Normal", icon: Hash, color: FOREST_MID },
    { value: "high", label: "High", icon: Pin, color: ACCENT_GOLD },
    { value: "urgent", label: "Urgent", icon: Zap, color: TERRACOTTA },
  ];

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
        className="fixed left-1/2 top-1/2 z-[71] w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: BORDER }}>
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl" style={{ background: FOREST_TINT }}>
              <MessageSquare className="h-4 w-4" style={{ color: FOREST }} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold" style={{ color: INK }}>New Message</h3>
              <p className="text-[11px]" style={{ color: MUTED }}>Compose a message to individuals or groups</p>
            </div>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-slate-100">
            <X className="h-4 w-4" style={{ color: MUTED }} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: MUTED }}>To</label>
            <div className="mt-1.5 relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: MUTED }} />
              <Input placeholder="Search people, departments, schools..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" autoFocus />
            </div>
            {selected.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selected.map(person => (
                  <span key={person.id} className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs" style={{ background: FOREST_TINT, color: FOREST }}>
                    <Avatar className="h-4 w-4">
                      <AvatarFallback style={{ background: "white", color: FOREST, fontSize: "0.5rem" }}>{getInitials(person.full_name)}</AvatarFallback>
                    </Avatar>
                    {person.full_name}
                    <button onClick={() => toggleSelect(person)} className="ml-0.5"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
            {searching && <div className="mt-2 text-xs" style={{ color: MUTED }}>Searching...</div>}
            {!searching && search.trim().length >= 2 && results.length === 0 && <div className="mt-2 text-xs" style={{ color: MUTED }}>No users found</div>}
            {results.length > 0 && (
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto rounded-lg border" style={{ borderColor: BORDER }}>
                {results.map(person => {
                  const isSelected = selected.find(p => p.id === person.id);
                  return (
                    <button key={person.id} onClick={() => toggleSelect(person)} className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-slate-50 ${isSelected ? "opacity-40" : ""}`}>
                      <Avatar className="h-8 w-8">
                        <AvatarFallback style={{ background: FOREST_TINT, color: FOREST, fontSize: "0.65rem", fontWeight: 600 }}>{getInitials(person.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium" style={{ color: INK }}>{person.full_name}</div>
                        <div className="text-[10px]" style={{ color: MUTED }}>{roleLabel(person.role)}</div>
                      </div>
                      {isSelected ? <Check className="h-4 w-4" style={{ color: FOREST_MID }} /> : <Plus className="h-4 w-4" style={{ color: MUTED }} />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selected.length > 1 && (
            <div>
              <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                <input type="checkbox" checked={isGroup} onChange={(e) => setIsGroup(e.target.checked)} className="rounded" />
                Create as group conversation
              </label>
              {isGroup && <Input placeholder="Group name (e.g. Finance Team)" value={groupName} onChange={(e) => setGroupName(e.target.value)} className="mt-2" />}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Priority</label>
            <div className="mt-1.5 flex gap-2">
              {priorityOptions.map(opt => {
                const Icon = opt.icon;
                const active = priority === opt.value;
                return (
                  <button key={opt.value} onClick={() => setPriority(opt.value)} className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium transition border ${active ? "border-2" : "border"}`} style={{ background: active ? `${opt.color}10` : "white", borderColor: active ? opt.color : BORDER, color: active ? opt.color : MUTED }}>
                    <Icon className="h-3.5 w-3.5" /> {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: MUTED }}>Message</label>
            <textarea placeholder="Type your message..." value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="mt-1.5 w-full resize-none rounded-xl border px-3 py-2 text-sm outline-none transition focus:ring-2" style={{ borderColor: BORDER }} />
          </div>
        </div>

        <div className="flex items-center justify-between border-t px-5 py-3" style={{ borderColor: BORDER }}>
          <div className="flex items-center gap-2 text-xs" style={{ color: MUTED }}>
            <Paperclip className="h-3.5 w-3.5" /><button className="hover:underline">Attach file</button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={handleSend} disabled={selected.length === 0 || !message.trim()} className="text-white" style={{ background: FOREST, border: "none" }}>
              <Send className="mr-1.5 h-3.5 w-3.5" /> Send
            </Button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
